import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { AiOutlineClose } from 'react-icons/ai';
import { ProfileSettingsModal, FieldSelectModern, Form, ToolLogo } from '../../components';
import { Form as FinalForm } from 'react-final-form';
import css from './ProfileSettingsToolsForm.css';

const ProfileSettingsToolsForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    tools,
    toolOptions,
    updateInProgress,
    intl,
  } = props;

  const [selectedTools, setSelectedTools] = useState(tools || []);

  // Reset values when the modal opens. Only on open: the page passes a freshly
  // mapped tools array on every render (resize, any Redux update), and
  // resetting then would drop the unsaved changes.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedTools(tools || []);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, tools]);

  const isDirty = JSON.stringify(selectedTools) !== JSON.stringify(tools || []);

  const handleSave = () => {
    // Convert keys to the storage format: [{ category: 'react' }, ...]
    const toolsData = selectedTools.map(key => ({ category: key }));
    const toolsKeys = selectedTools;
    onSubmit({ tools: toolsData, toolsKeys });
    onClose();
  };

  const handleShowSpaceHome = () => {
    if (typeof window === 'object' && window.Intercom) {
      window.Intercom('showSpace', 'home');
    }
  };

  // Filter out already-selected tools from the dropdown options
  const selectOptions = toolOptions
    ? toolOptions
        .filter(opt => !selectedTools.includes(opt.key))
        .map(opt => ({ value: opt.key, label: opt.label }))
    : [];

  const handleToolSelect = option => {
    if (!option) return;
    setSelectedTools([...selectedTools, option.value]);
  };

  const removeTool = toolKey => {
    setSelectedTools(selectedTools.filter(k => k !== toolKey));
  };

  const getOption = key => toolOptions?.find(o => o.key === key);

  return (
    <ProfileSettingsModal
      id="tools"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      title={intl.formatMessage({ id: 'ProfileSettingsPage.editToolsTitle' })}
      subtitle={
        <FormattedMessage
          id="ProfileSettingsPage.editToolsSubtitle"
          values={{
            a: chunks => (
              <span className={css.chatLink} onClick={handleShowSpaceHome}>
                {chunks}
              </span>
            ),
          }}
        />
      }
      onSubmit={handleSave}
      submitDisabled={!isDirty}
      submitInProgress={updateInProgress}
      submitLabel={intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' })}
    >
      <FinalForm
        onSubmit={() => {}}
        initialValues={{ toolSearch: null }}
        render={({ form }) => (
          <Form className={css.formContent} onSubmit={() => {}}>
            <div>
              <FieldSelectModern
                name="toolSearch"
                id="toolSearch"
                label={intl.formatMessage({ id: 'ProfileSettingsPage.toolsLabel' })}
                options={selectOptions}
                isSearchable={true}
                isClearable={false}
                onChange={option => {
                  if (option) {
                    handleToolSelect(option);
                    form.change('toolSearch', null);
                  }
                }}
              />
              {selectedTools.length > 0 ? (
                <div className={css.toolTags}>
                  {selectedTools.map(toolKey => (
                    <span key={toolKey} className={css.toolTag}>
                      <ToolLogo logoUrl={getOption(toolKey)?.logoUrl} className={css.toolLogo} />
                      {getOption(toolKey)?.label || toolKey}
                      <button
                        type="button"
                        className={css.toolTagRemove}
                        onClick={() => removeTool(toolKey)}
                      >
                        <AiOutlineClose className={css.toolTagRemoveIcon} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </Form>
        )}
      />
    </ProfileSettingsModal>
  );
};

ProfileSettingsToolsForm.defaultProps = {
  tools: [],
  toolOptions: [],
  updateInProgress: false,
};

ProfileSettingsToolsForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  tools: PropTypes.array,
  toolOptions: PropTypes.array,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsToolsForm);
